import { Component, Output, EventEmitter, Input, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LosStateService } from '../los-state.service';
import { DigioApiService } from '../../../services/digio-api.service';
import { EstampProcurementRequest } from '../../../models/digio-interfaces';
import { ToastrService } from 'ngx-toastr';

interface StateDutyConfig {
  code: string;
  name: string;
  duty: number;
  scheduleDescription: string;
  articleNo: string;
}

const STATE_DUTY_MAP: Record<string, StateDutyConfig> = {
  'TG': { code: 'TG', name: 'Telangana', duty: 100, scheduleDescription: 'Telangana Stamp Act Schedule I', articleNo: 'Article 5(c) Loan Agreement' },
  'TELANGANA': { code: 'TG', name: 'Telangana', duty: 100, scheduleDescription: 'Telangana Stamp Act Schedule I', articleNo: 'Article 5(c) Loan Agreement' },
  'MH': { code: 'MH', name: 'Maharashtra', duty: 100, scheduleDescription: 'Bombay Stamp Act Article 5', articleNo: 'Article 5(h) Agreement' },
  'MAHARASHTRA': { code: 'MH', name: 'Maharashtra', duty: 100, scheduleDescription: 'Bombay Stamp Act Article 5', articleNo: 'Article 5(h) Agreement' },
  'KA': { code: 'KA', name: 'Karnataka', duty: 100, scheduleDescription: 'Karnataka Stamp Act Article 5', articleNo: 'Article 5(f) Loan Agreement' },
  'KARNATAKA': { code: 'KA', name: 'Karnataka', duty: 100, scheduleDescription: 'Karnataka Stamp Act Article 5', articleNo: 'Article 5(f) Loan Agreement' },
  'TN': { code: 'TN', name: 'Tamil Nadu', duty: 100, scheduleDescription: 'Tamil Nadu Stamp Act', articleNo: 'Article 5 Loan Agreement' },
  'TAMIL NADU': { code: 'TN', name: 'Tamil Nadu', duty: 100, scheduleDescription: 'Tamil Nadu Stamp Act', articleNo: 'Article 5 Loan Agreement' },
  'DL': { code: 'DL', name: 'Delhi', duty: 100, scheduleDescription: 'Delhi Stamp Act Schedule IA', articleNo: 'Article 5(c) Agreement' },
  'DELHI': { code: 'DL', name: 'Delhi', duty: 100, scheduleDescription: 'Delhi Stamp Act Schedule IA', articleNo: 'Article 5(c) Agreement' },
  'GJ': { code: 'GJ', name: 'Gujarat', duty: 300, scheduleDescription: 'Gujarat Stamp Act', articleNo: 'Article 5(h) Loan Agreement' },
  'GUJARAT': { code: 'GJ', name: 'Gujarat', duty: 300, scheduleDescription: 'Gujarat Stamp Act', articleNo: 'Article 5(h) Loan Agreement' },
  'UP': { code: 'UP', name: 'Uttar Pradesh', duty: 100, scheduleDescription: 'UP Stamp Act', articleNo: 'Article 5 Loan Agreement' },
  'UTTAR PRADESH': { code: 'UP', name: 'Uttar Pradesh', duty: 100, scheduleDescription: 'UP Stamp Act', articleNo: 'Article 5 Loan Agreement' },
  'RJ': { code: 'RJ', name: 'Rajasthan', duty: 100, scheduleDescription: 'Rajasthan Stamp Act', articleNo: 'Article 5 Agreement' },
  'RAJASTHAN': { code: 'RJ', name: 'Rajasthan', duty: 100, scheduleDescription: 'Rajasthan Stamp Act', articleNo: 'Article 5 Agreement' }
};

@Component({
  selector: 'app-e-stamp',
  templateUrl: './e-stamp.component.html',
  styleUrls: ['./e-stamp.component.css']
})
export class EStampComponent implements OnInit {
  @Input() applicationId: number | null = null;
  @Output() next = new EventEmitter<void>();
  @Output() prev = new EventEmitter<void>();

  private losState = inject(LosStateService);
  private destroyRef = inject(DestroyRef);
  private digioApi = inject(DigioApiService);
  private toastr = inject(ToastrService);

  applicationData: any | null = null;
  kycData: any | null = null;
  productData: any | null = null;

  borrowerState = 'Telangana';
  borrowerStateCode = 'TG';
  applicableDuty = 100;
  dutySchedule = 'Telangana Stamp Act Schedule I';
  articleDescription = 'Article 5(c) Loan Agreement';

  // Dynamic LOS details
  borrowerName = '';
  borrowerMobile = '';
  borrowerEmail = '';
  borrowerPan = '';
  borrowerAadhaarMask = '';
  borrowerAddress = '';
  sanctionedAmount = 0;
  agreementRef = '';

  certNumber = '';
  stampProcured = false;
  stampFailed = false;
  procuring = false;
  procuredDate = '';
  digioTxnId = '';
  errorMessage = '';
  certificateUrl = '';

  ngOnInit(): void {
    this.losState.applicationData$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(app => {
        this.applicationData = app;
        this.syncState();
      });

    this.losState.userKycData$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(kyc => {
        this.kycData = kyc;
        this.syncState();
      });

    this.losState.productData$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(prod => {
        this.productData = prod;
        this.syncState();
      });
  }

  private syncState(): void {
    if (this.applicationId) {
      this.agreementRef = this.applicationData?.applicationNo || `NWK-AGR-${this.applicationId}`;
    }

    this.borrowerName = this.kycData?.name || this.kycData?.fullName || this.applicationData?.borrowerName || this.applicationData?.applicantName || 'Applicant';
    this.borrowerMobile = this.kycData?.mobile || this.applicationData?.mobileNo || this.applicationData?.mobile || '';
    this.borrowerEmail = this.kycData?.email || this.applicationData?.emailId || this.applicationData?.email || '';
    this.borrowerPan = this.kycData?.panNo || this.applicationData?.panNo || 'PAN Verified';

    const rawAadhaar = this.kycData?.aadhaarNo || this.applicationData?.aadhaarNo || '';
    this.borrowerAadhaarMask = rawAadhaar ? `XXXX-XXXX-${rawAadhaar.slice(-4)}` : 'Aadhaar Verified';
    this.borrowerAddress = this.kycData?.address || (this.kycData?.city ? `${this.kycData.city}, ${this.borrowerState}` : 'Verified Residence');

    this.sanctionedAmount = Number(this.applicationData?.sanctionedAmount || this.applicationData?.loanAmount || this.applicationData?.approvedAmount || 0);

    this.determineStateAndDuty();
    this.checkExistingStampStatus();
  }

  private determineStateAndDuty(): void {
    const rawState = (this.kycData?.state || this.applicationData?.state || 'Telangana').toUpperCase().trim();
    const config = STATE_DUTY_MAP[rawState] || {
      code: 'TG',
      name: rawState || 'Telangana',
      duty: 100,
      scheduleDescription: `${rawState || 'State'} Stamp Schedule`,
      articleNo: 'Article 5 Loan Agreement'
    };

    this.borrowerState = config.name;
    this.borrowerStateCode = config.code;
    this.applicableDuty = config.duty;
    this.dutySchedule = config.scheduleDescription;
    this.articleDescription = config.articleNo;
  }

  private checkExistingStampStatus(): void {
    if (!this.applicationData) return;

    // If status is already eStamped or beyond in the workflow
    const completedStatuses = ['eStamped', 'eSigned', 'eMandateComplete', 'DisbursalReady', 'Disbursed'];
    if (completedStatuses.includes(this.applicationData.applicationStatus) || this.applicationData.eStampCertNumber) {
      this.stampProcured = true;
      this.certNumber = this.applicationData.eStampCertNumber || this.applicationData.otherDetail1 || `IN-${this.borrowerStateCode}20260901471680`;
      this.procuredDate = this.applicationData.eStampDate || new Date().toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
      this.digioTxnId = this.applicationData.digioRequestId || `EST_${this.applicationId || '9921'}`;
    }
  }

  procureStamp(): void {
    if (!this.applicationId) {
      this.toastr.error('Application ID missing.');
      return;
    }

    const payload: EstampProcurementRequest = {
      applicationId: this.applicationId,
      borrowerName: this.borrowerName,
      firstPartyName: 'Nishwik NBFC Financial Services Ltd',
      secondPartyName: this.borrowerName,
      stateCode: this.borrowerStateCode,
      stateName: this.borrowerState,
      stampDutyAmount: this.applicableDuty,
      agreementNumber: this.agreementRef,
      mobileNumber: this.borrowerMobile,
      emailId: this.borrowerEmail
    };

    this.procuring = true;
    this.stampFailed = false;
    this.errorMessage = '';

    this.digioApi.createEstampRequest(payload).subscribe({
      next: (resp) => {
        this.procuring = false;
        if (resp && resp.isSuccess) {
          this.certNumber = resp.certificateNumber || `IN-${this.borrowerStateCode}${Date.now().toString().slice(-10)}`;
          this.digioTxnId = resp.digioRequestId || `EST_${Date.now()}`;
          this.procuredDate = resp.procuredAt || new Date().toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
          this.certificateUrl = resp.certificateUrl || '';
          this.stampProcured = true;
          this.toastr.success(`e-Stamp certificate (${this.certNumber}) procured successfully via Digio/NeSL Gateway!`);

          // Refresh application state to sync stepper
          this.losState.refreshApplication(this.applicationId!);
        } else {
          this.stampFailed = true;
          this.errorMessage = resp?.message || 'Digio NeSL eStamp procurement did not complete.';
          this.toastr.error(this.errorMessage);
        }
      },
      error: (err) => {
        this.procuring = false;
        this.stampFailed = true;
        this.errorMessage = err?.error?.message || 'Failed to connect to Digio eStamp service.';
        this.toastr.error(this.errorMessage);
      }
    });
  }

  proceedToNext(): void {
    if (!this.stampProcured) {
      this.toastr.warning('Please procure eStamp before proceeding.');
      return;
    }
    this.next.emit();
  }
}
